import React from 'react';
import { commonEnums, objectUtils } from '@codametrix/ui-common';
import { HoldReasonLabel } from '../case-hold/case-hold';
import {
  ActivityCodeChip,
  ActivityEntry
} from './activity-entry';
import './styles/index.scss';
const { DocumentStatus, HoldReasonType } = commonEnums;

export enum UserActivityActionType {
  UPDATE_CODE = 'UPDATE_CODE',
  REMOVE_CODE = 'REMOVE_CODE',
  ADD_CODE = 'ADD_CODE',

  SUBMIT_CODES = 'SUBMIT_CODES',
  CANCEL_CASE = 'CANCEL_CASE',

  ADD_INVOICE = 'ADD_INVOICE',
  UPDATE_INVOICE = 'UPDATE_INVOICE',
  DELETE_INVOICE = 'DELETE_INVOICE',

  ADD_TRANSACTION = 'ADD_TRANSACTION',
  DELETE_TRANSACTION = 'DELETE_TRANSACTION',
  UPDATE_CHARGEABLE = 'UPDATE_CHARGEABLE',

  ADD_DOCUMENT_IMPROVEMENT = 'ADD_DOCUMENT_IMPROVEMENT',
  UPDATE_DOCUMENT_IMPROVEMENT = 'UPDATE_DOCUMENT_IMPROVEMENT',
  DELETE_DOCUMENT_IMPROVEMENT = 'DELETE_DOCUMENT_IMPROVEMENT',
  ADD_PROVIDER = 'ADD_PROVIDER',
  UPDATE_PROVIDER = 'UPDATE_PROVIDER',
  DELETE_PROVIDER = 'DELETE_PROVIDER',

  HOLD_PROCESS_INSTANCE = 'HOLD_PROCESS_INSTANCE',

  UPDATE_FIELD = 'UPDATE_FIELD',

  SPLIT_CASE = 'SPLIT_CASE',
  MERGE_CASE = 'MERGE_CASE',

  CREATE_CASE = 'CREATE_CASE',
  MANUAL_ASSIGNMENT = 'MANUAL_ASSIGNMENT',
  REFRESH_CASE = 'REFRESH_CASE',

  DISMISS_LATE_CASE = 'DISMISS_LATE_CASE',
  COMMENT_ON_CASE = 'COMMENT_ON_CASE'
}

const invoiceChangeRepr = (activity: CMxAPI.UserActivity) => {
  if (!activity.updatedInvoiceFieldName) {
    return '';
  }
  return activity.updatedInvoiceFieldName
    .replace(/([A-Z])/g, ' $1')
    .trim()
    .toLowerCase();
};

enum ActivityCodeType {
  PROCEDURE = 'ProcedureCode',
  DIAGNOSIS = 'DiagnosisCode',
  MODIFIER = 'ModifierCode'
}

function entityLabelForCode(codeType: string | null | undefined): string {
  if (codeType === ActivityCodeType.PROCEDURE) {
    return 'Procedure';
  }
  if (codeType === ActivityCodeType.DIAGNOSIS) {
    return 'Diagnosis';
  }
  if (codeType === ActivityCodeType.MODIFIER) {
    return 'Modifier';
  }
  return 'Code';
}

const reasonsOf = (a: CMxAPI.UserActivity) =>
  (a.reasons ?? []).filter((r) => r?.label?.trim());

const detailOf = (a: CMxAPI.UserActivity) => {
  const lines: React.ReactNode[] = [];
  if (a.message) {
    lines.push(a.message);
  }
  if (a.caseContext) {
    lines.push(`Context: ${a.caseContext}`);
  }
  if (lines.length === 0) {
    return undefined;
  }
  return (
    <>
      {lines.map((line, i) => (
        <React.Fragment key={i}>
          {i > 0 ? <br /> : null}
          {line}
        </React.Fragment>
      ))}
    </>
  );
};

function humanizeUnderscores(s?: string | null): string {
  if (!s) {
    return '';
  }
  return s
    .split('_')
    .filter(Boolean)
    .map(
      w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()
    )
    .join(' ');
}

const userActivityFormatter = (activity: CMxAPI.UserActivity): any => {
  const actor = activity.username ?? '';
  const ts = activity.timestamp;

  if (activity.actionType === UserActivityActionType.ADD_CODE) {
    return (
      <ActivityEntry
        timestamp={ts}
        actor={actor}
        primary={
          <>
            {entityLabelForCode(activity.codeType)}{' '}
            <ActivityCodeChip>{activity.code}</ActivityCodeChip> added
          </>
        }
      />
    );
  }

  if (activity.actionType === UserActivityActionType.REMOVE_CODE) {
    return (
      <ActivityEntry
        timestamp={ts}
        actor={actor}
        primary={
          <>
            {entityLabelForCode(activity.codeType)}{' '}
            <ActivityCodeChip>{activity.code}</ActivityCodeChip> removed
          </>
        }
      />
    );
  }

  if (activity.actionType === UserActivityActionType.UPDATE_CODE) {
    if (
      activity.codeType === ActivityCodeType.DIAGNOSIS ||
      activity.codeType === ActivityCodeType.MODIFIER
    ) {
      return (
        <ActivityEntry
          timestamp={ts}
          actor={actor}
          primary={
            <>
              {entityLabelForCode(activity.codeType)}{' '}
              <ActivityCodeChip>{activity.code}</ActivityCodeChip> reordered
            </>
          }
        />
      );
    }
    return (
      <ActivityEntry
        timestamp={ts}
        actor={actor}
        primary={
          <>
            {entityLabelForCode(activity.codeType)}{' '}
            <ActivityCodeChip>{activity.code}</ActivityCodeChip> updated to{' '}
            <ActivityCodeChip>{activity.units} units</ActivityCodeChip>
          </>
        }
      />
    );
  }

  if (activity.actionType === UserActivityActionType.SUBMIT_CODES) {
    if (activity.withOverride) {
      return (
        <ActivityEntry
          timestamp={ts}
          actor={actor}
          primary={<>Charges submitted with override</>}
          detail={activity.message ? <>{activity.message}</> : undefined}
        />
      );
    }
    return (
      <ActivityEntry
        timestamp={ts}
        actor={actor}
        primary={<>Charges submitted</>}
      />
    );
  }

  if (activity.actionType === UserActivityActionType.REFRESH_CASE) {
    return (
      <ActivityEntry
        timestamp={ts}
        actor={actor}
        primary={<>Case refreshed</>}
      />
    );
  }

  if (activity.actionType === UserActivityActionType.CANCEL_CASE) {
    return (
      <ActivityEntry
        timestamp={ts}
        actor={actor}
        primary={<>Case cancelled</>}
      />
    );
  }

  if (activity.actionType === UserActivityActionType.ADD_INVOICE) {
    return (
      <ActivityEntry
        timestamp={ts}
        actor={actor}
        primary={<>Invoice added</>}
      />
    );
  }

  if (activity.actionType === UserActivityActionType.DELETE_INVOICE) {
    return (
      <ActivityEntry
        timestamp={ts}
        actor={actor}
        primary={<>Invoice removed</>}
      />
    );
  }

  if (activity.actionType === UserActivityActionType.UPDATE_INVOICE) {
    const fieldLabel = invoiceChangeRepr(activity).trim();
    const val = activity.updatedInvoiceFieldValue;
    return (
      <ActivityEntry
        timestamp={ts}
        actor={actor}
        primary={
          <>
            Invoice updated
            {fieldLabel ? (
              <>
                {' '}
                <ActivityCodeChip>{fieldLabel}</ActivityCodeChip>
              </>
            ) : null}
            {val !== null && val !== undefined && `${val}` !== '' ? (
              <>
                {' '}
                <ActivityCodeChip>{String(val)}</ActivityCodeChip>
              </>
            ) : null}
          </>
        }
      />
    );
  }

  if (activity.actionType === UserActivityActionType.ADD_TRANSACTION) {
    return (
      <ActivityEntry
        timestamp={ts}
        actor={actor}
        primary={
          activity.sequenceNumber != null ? (
            <>
              Charge{' '}
              <ActivityCodeChip>{String(activity.sequenceNumber)}</ActivityCodeChip>{' '}
              added
            </>
          ) : (
            <>Charge added</>
          )
        }
      />
    );
  }

  if (activity.actionType === UserActivityActionType.DELETE_TRANSACTION) {
    return (
      <ActivityEntry
        timestamp={ts}
        actor={actor}
        primary={
          activity.sequenceNumber != null ? (
            <>
              Charge{' '}
              <ActivityCodeChip>{String(activity.sequenceNumber)}</ActivityCodeChip>{' '}
              removed
            </>
          ) : (
            <>Charge removed</>
          )
        }
      />
    );
  }

  if (activity.actionType === UserActivityActionType.UPDATE_CHARGEABLE) {
    const reasons = reasonsOf(activity);
    if (!activity.isChargeable && reasons.length) {
      return (
        <ActivityEntry
          timestamp={ts}
          actor={actor}
          primary={
            <>
              Case excluded{' '}
              {reasons.map((r) => (
                <ActivityCodeChip key={r.key}>{r.label}</ActivityCodeChip>
              ))}
            </>
          }
          detail={detailOf(activity)}
        />
      );
    }
    return (
      <ActivityEntry
        timestamp={ts}
        actor={actor}
        primary={
          <>
            Case marked{' '}
            {activity?.isChargeable ? (
              <>chargeable</>
            ) : (
              <>non-chargeable</>
            )}
          </>
        }
      />
    );
  }

  if (
    activity.actionType === UserActivityActionType.ADD_DOCUMENT_IMPROVEMENT
  ) {
    return (
      <ActivityEntry
        timestamp={ts}
        actor={actor}
        primary={
          <>
            Improvement{' '}
            <ActivityCodeChip>{activity.documentImprovement}</ActivityCodeChip>{' '}
            added
          </>
        }
      />
    );
  }

  if (
    activity.actionType === UserActivityActionType.UPDATE_DOCUMENT_IMPROVEMENT
  ) {
    const fieldName = activity?.updatedDocumentImprovementFieldName;
    const fieldValue = activity?.updatedDocumentImprovementFieldValue;
    const filedText =
      fieldName === 'charge_code' ? 'charge code' : 'potential charge code';
    return (
      <ActivityEntry
        timestamp={ts}
        actor={actor}
        primary={
          <>
            Improvement{' '}
            <ActivityCodeChip>{activity.documentImprovement}</ActivityCodeChip>{' '}
            updated —{' '}
            {fieldValue ? (
              <>
                added <ActivityCodeChip>{fieldValue}</ActivityCodeChip>{' '}
                {filedText}
              </>
            ) : (
              <>removed {filedText}</>
            )}
          </>
        }
      />
    );
  }

  if (
    activity.actionType === UserActivityActionType.DELETE_DOCUMENT_IMPROVEMENT
  ) {
    return (
      <ActivityEntry
        timestamp={ts}
        actor={actor}
        primary={
          <>
            Improvement{' '}
            <ActivityCodeChip>{activity.documentImprovement}</ActivityCodeChip>{' '}
            deleted
          </>
        }
      />
    );
  }

  if (activity.actionType === UserActivityActionType.ADD_PROVIDER) {
    return (
      <ActivityEntry
        timestamp={ts}
        actor={actor}
        primary={
          <>
            Provider{' '}
            <ActivityCodeChip>
              {objectUtils.capitalize(
                activity.documentImprovementProviderName ?? ''
              )}
            </ActivityCodeChip>{' '}
            added to improvement{' '}
            <ActivityCodeChip>{activity.documentImprovement}</ActivityCodeChip>
          </>
        }
      />
    );
  }

  if (activity.actionType === UserActivityActionType.DELETE_PROVIDER) {
    return (
      <ActivityEntry
        timestamp={ts}
        actor={actor}
        primary={
          <>
            Provider{' '}
            <ActivityCodeChip>
              {objectUtils.capitalize(
                activity.documentImprovementProviderName ?? ''
              )}
            </ActivityCodeChip>{' '}
            removed from improvement{' '}
            <ActivityCodeChip>{activity.documentImprovement}</ActivityCodeChip>
          </>
        }
      />
    );
  }

  if (activity.actionType === UserActivityActionType.UPDATE_PROVIDER) {
    return (
      <ActivityEntry
        timestamp={ts}
        actor={actor}
        primary={
          <>
            Provider{' '}
            <ActivityCodeChip>
              {objectUtils.capitalize(
                activity.documentImprovementProviderName ?? ''
              )}
            </ActivityCodeChip>{' '}
            updated status{' '}
            <ActivityCodeChip>
              {objectUtils.capitalize(
                activity.documentImprovementProviderQueryStatus?.replace(
                  /_/g,
                  ' '
                ) ?? ''
              )}
            </ActivityCodeChip>{' '}
            for improvement{' '}
            <ActivityCodeChip>{activity.documentImprovement}</ActivityCodeChip>
          </>
        }
      />
    );
  }

  if (activity.actionType === UserActivityActionType.HOLD_PROCESS_INSTANCE) {
    const onHold = activity.holdReason !== HoldReasonType.OFF_HOLD;
    const reasons = reasonsOf(activity);
    return (
      <ActivityEntry
        timestamp={ts}
        actor={actor}
        primary={
          onHold ? (
            <>
              Case placed on hold{' '}
              {reasons.length ? (
                reasons.map((r) => (
                  <ActivityCodeChip key={r.key}>{r.label}</ActivityCodeChip>
                ))
              ) : (
                <ActivityCodeChip>
                  {HoldReasonLabel[activity.holdReason]}
                </ActivityCodeChip>
              )}
            </>
          ) : (
            <>Case taken off hold</>
          )
        }
        detail={detailOf(activity)}
      />
    );
  }

  if (activity.actionType === UserActivityActionType.UPDATE_FIELD) {
    const fieldName = humanizeUnderscores(activity.updatedFieldName);
    const fieldValue = humanizeUnderscores(activity.updatedFieldValue);
    return (
      <ActivityEntry
        timestamp={ts}
        actor={actor}
        primary={
          <>
            Field <ActivityCodeChip>{fieldName || '—'}</ActivityCodeChip> set
            to <ActivityCodeChip>{fieldValue || '—'}</ActivityCodeChip>
          </>
        }
      />
    );
  }

  if (activity.actionType === UserActivityActionType.SPLIT_CASE) {
    return (
      <ActivityEntry
        timestamp={ts}
        actor={actor}
        primary={<>Case split</>}
      />
    );
  }

  if (activity.actionType === UserActivityActionType.MERGE_CASE) {
    return (
      <ActivityEntry
        timestamp={ts}
        actor={actor}
        primary={<>Case merged</>}
      />
    );
  }

  if (activity.actionType === UserActivityActionType.CREATE_CASE) {
    const hasComment = !!activity.message;
    return (
      <ActivityEntry
        timestamp={ts}
        actor={actor}
        primary={<>Case created</>}
        detail={
          hasComment ? (
            <>
              Comment: <ActivityCodeChip>{activity.message}</ActivityCodeChip>
            </>
          ) : undefined
        }
      />
    );
  }

  if (activity.actionType === UserActivityActionType.MANUAL_ASSIGNMENT) {
    const reasons = reasonsOf(activity);
    if (reasons.length) {
      return (
        <ActivityEntry
          timestamp={ts}
          actor={actor}
          primary={
            <>
              Case transferred to{' '}
              <ActivityCodeChip>
                {activity.reassignedUsername}
              </ActivityCodeChip>{' '}
              {reasons.map((r) => (
                <ActivityCodeChip key={r.key}>{r.label}</ActivityCodeChip>
              ))}
            </>
          }
          detail={detailOf(activity)}
        />
      );
    }
    return (
      <ActivityEntry
        timestamp={ts}
        actor={actor}
        primary={
          <>
            Case reassigned to{' '}
            <ActivityCodeChip>
              {activity.reassignedUsername}
            </ActivityCodeChip>
          </>
        }
      />
    );
  }

  if (activity.actionType === UserActivityActionType.DISMISS_LATE_CASE) {
    return (
      <ActivityEntry
        timestamp={ts}
        actor={actor}
        primary={<>Late case notice dismissed</>}
      />
    );
  }

  if (activity.actionType === UserActivityActionType.COMMENT_ON_CASE) {
    return (
      <ActivityEntry
        timestamp={ts}
        actor={actor}
        primary={
          <>
            Comment added{' '}
            <ActivityCodeChip>{activity.message}</ActivityCodeChip>
          </>
        }
      />
    );
  }

  return (
    <ActivityEntry
      timestamp={ts}
      actor={actor}
      primary={
        <>
          Activity{' '}
          <ActivityCodeChip>{activity.actionType}</ActivityCodeChip>
          {activity.code ? (
            <>
              {' '}
              <ActivityCodeChip>{activity.code}</ActivityCodeChip>
            </>
          ) : null}
        </>
      }
    />
  );
};

export { userActivityFormatter };
